import { useDisclosure } from '@chakra-ui/react'
import { Dispatch, FC, SetStateAction } from 'react'

import DialogWindow from '@/components/DialogWindow'
import { Button, Typography } from '@/components/ui'
import CustomSelectLanguage from '@/components/ui/select/CustomSelectLanguage'

import Basket from '@/assets/icons/basket2.svg'

import { PLATFORMS_LABELS } from '@/constants/languages'
import { IOption } from '@/types/selectOption.interface'
import { IShotlist } from '@/types/shotlist.interface'
import { ILanguage } from '@/types/task.interface'
import {
	ISelectedPlatform,
	TPlatformLabel,
	TPlatformValue
} from '@/types/uploadForm.interface'

import { getSourceNameFromShotlist } from '../helpers/getSourceNameFromShotlist'

import FormAutoFilling from './FormAutoFilling'
import FormHeaderStatus from './FormHeaderStatus'

interface IFormHeaderProps {
	selectedPlatformLabel: TPlatformLabel | ''
	selectedPlatform: TPlatformValue | null
	isUploading: boolean
	isTranscribing?: boolean
	deletePlatform: (value: TPlatformValue) => void
	handleTranscribe?: (value: string) => void
	handleTranslate?: (value: string) => void
	handleUploadSrt?: (value: File) => void
	languages: ILanguage[]
	selectedTranscribingLang?: ILanguage | null
	setSelectedTranscribingLang?: Dispatch<SetStateAction<ILanguage | null>>
	isTranslating?: boolean
	taskId?: string
	platformState?: ISelectedPlatform
	selectedPlatformList?: ISelectedPlatform[]
	availableSourceLanguages?: IOption[]
	currentPlatform?: ISelectedPlatform
	shotlist?: IShotlist | null
	pendingSrtFileName?: string
	handleCloseErrorMessage?: () => void
	handleDeleteTranslation?: () => void
	onStopTranscription?: () => void
	handleAutoFill: () => void
	sourceAutoFillLang: TPlatformValue | ''
	setSourceAutoFillLang: Dispatch<SetStateAction<TPlatformValue | ''>>
}

const FormHeader: FC<IFormHeaderProps> = ({
	selectedPlatformLabel,
	selectedPlatform,
	isUploading,
	isTranscribing,
	deletePlatform,
	handleTranscribe,
	handleTranslate,
	handleUploadSrt,
	languages,
	selectedTranscribingLang,
	setSelectedTranscribingLang,
	isTranslating,
	taskId,
	platformState,
	selectedPlatformList,
	availableSourceLanguages,
	currentPlatform,
	shotlist,
	pendingSrtFileName,
	handleCloseErrorMessage,
	handleDeleteTranslation,
	onStopTranscription,
	handleAutoFill,
	sourceAutoFillLang,
	setSourceAutoFillLang
}) => {
	const deletePlatformDialog = useDisclosure()
	const selectLanguageDialog = useDisclosure()

	const sourceName = getSourceNameFromShotlist(
		shotlist,
		selectedPlatform,
		selectedPlatformList,
		languages
	)
	const transcribingLabel =
		selectedTranscribingLang?.language || pendingSrtFileName || sourceName

	const headerState = platformState?.headerState || 'idle'
	const isProcessing = headerState === 'processing'

	const availableFillLanguages = selectedPlatformList
		?.filter(lang => lang.value !== selectedPlatform)
		.map(lang => {
			return { value: lang.value, label: PLATFORMS_LABELS[lang.value] }
		})

	const onTranscribe = () => {
		if (isProcessing) return

		selectLanguageDialog.onClose()
		if (!selectedTranscribingLang?.code) return
		handleTranscribe?.(selectedTranscribingLang.code)
	}

	const handleCreate = (sourceLanguage: string) => {
		if (isProcessing) return

		const isAnyDone = (selectedPlatformList || []).some(
			p => p.headerState === 'done'
		)

		if (isAnyDone) handleTranslate?.(sourceLanguage)
		else if (!isAnyDone && selectedTranscribingLang) {
			handleTranslate?.(selectedTranscribingLang.code)
		} else selectLanguageDialog.onOpen()
	}

	const deletePlatformDisabled =
		!selectedPlatform ||
		isTranslating ||
		isProcessing ||
		currentPlatform.processing === 'autofill'

	const firstPlatform = selectedPlatformList[0].value === selectedPlatform

	return (
		<div>
			{/* Deleting platform modal window */}
			<DialogWindow
				handleClick={() => {
					if (!selectedPlatform) return
					deletePlatform?.(selectedPlatform)
				}}
				open={deletePlatformDialog.open}
				onClose={deletePlatformDialog.onClose}
				titleText='Delete platform?'
				yesButtonText='Yes'
				noButtonClassname='bg-gray-100 hover:bg-gray-200'
				yesButtonClassname='bg-gray-100 hover:bg-gray-200'
				yesButtonClassnameText='text-red-450'
			/>

			{/* Select language for transcribation modal window */}
			<DialogWindow
				handleClick={onTranscribe}
				open={selectLanguageDialog.open}
				onClose={() => {
					setSelectedTranscribingLang(null)
					selectLanguageDialog.onClose()
				}}
				titleText='Select source language'
				body={
					<CustomSelectLanguage
						value={selectedTranscribingLang}
						onChange={setSelectedTranscribingLang}
						options={languages ?? []}
						placeholder='Select language'
					/>
				}
				noButtonText='Cancel'
				yesButtonText='Create'
			/>
			<div className='flex justify-between items-center mb-[34.5px] pr-4'>
				{/* Platform name title*/}
				<Typography variant='text-semibold' className='text-[32px] !font-[700]'>
					{selectedPlatformLabel}
				</Typography>

				{/* Delete platform button */}
				{!firstPlatform && (
					<Button
						onClick={deletePlatformDialog.onOpen}
						disabled={deletePlatformDisabled}
					>
						<Basket />
					</Button>
				)}
			</div>

			{/* Autofilling animation, language selection and status */}
			<FormAutoFilling
				currentPlatform={currentPlatform}
				availableFillLanguages={availableFillLanguages}
				handleAutoFill={handleAutoFill}
				sourceAutoFillLang={sourceAutoFillLang}
				setSourceAutoFillLang={setSourceAutoFillLang}
				isProcessing={isProcessing}
			/>
			<Typography
				variant='text-semibold'
				className='text-[20px] leading-[32px] !font-bold'
			>
				Shotlist
			</Typography>
			{sourceName && (
				<Typography
					variant='text'
					className='self-start mb-2 text-[16px] !font-[400] leading-[24px] text-gray-450'
				>
					Source: {sourceName}
				</Typography>
			)}

			{/* Transcribing and translating animations, language selection and statuses */}
			<FormHeaderStatus
				platformState={platformState}
				isUploading={isUploading}
				sourceName={sourceName}
				transcribingLabel={transcribingLabel}
				taskId={taskId}
				onCreate={handleCreate}
				handleUploadSrt={handleUploadSrt}
				selectedPlatform={selectedPlatform}
				availableSourceLanguages={availableSourceLanguages}
				selectedTranscribingLang={selectedTranscribingLang}
				handleCloseErrorMessage={handleCloseErrorMessage}
				handleDeleteTranslation={handleDeleteTranslation}
				onStopTranscription={onStopTranscription}
				selectedPlatformList={selectedPlatformList}
				isProcessing={currentPlatform.processing === 'autofill'}
				isTranscribing={isTranscribing}
			/>
		</div>
	)
}

export default FormHeader
